import React from 'react';
import {
  CameraImageResponse,
  PlanogramComplianceResponse,
  ProductRecognitionBBox,
  ProductRecognitionMask,
  ProductRecognitionPolygon,
} from '../../../types';
import { PogProductContent } from '../../store-details/components/PogProductContent';
import { ProductRecognitionDetectionContent } from '../../store-details/components/ProductRecognitionDetectionContent';
import { GtPogStatusEditor, PogResultStatus } from './GtPogStatusEditor';
import { GtRogStatusEditor } from './GtRogStatusEditor';
import { parsePositionKey, RogItemStatusValue } from './rogStatusUtils';

interface GtImageItemDetailsDialogProps {
  item: ProductRecognitionBBox | ProductRecognitionPolygon | ProductRecognitionMask | any;
  cameraImageElement?: HTMLImageElement;
  isOpen: boolean;
  onClose: () => void;
  layerBboxColor?: string;
  chainId?: number;
  storeId?: number;
  storeName?: string;
  onUpcSubmit?: (upc: string) => void;
  shouldShowEmbeddings?: boolean;
  planogramComplianceData?: PlanogramComplianceResponse | null;
  cameraImage?: CameraImageResponse | null;
  cartonBoxes?: ProductRecognitionBBox[] | null;
  pogStatus?: PogResultStatus | null;
  onPogStatusApply?: (ilItemId: number, status: PogResultStatus) => void;
  rogPositionKey?: string | null;
  rogPositionStatuses?: RogItemStatusValue[];
  rogPositionFinalItemNumber?: string | null;
  rogPositionPrUuidCount?: number;
  onRogStatusApply?: (positionKey: string, statuses: RogItemStatusValue[]) => void;
  onRemoveRogPosition?: (mod: number, shelf: number, column: number) => void;
  onMoveRogPosition?: (
    fromMod: number,
    fromShelf: number,
    fromColumn: number,
    toMod: number,
    toShelf: number,
    toColumn: number
  ) => void;
  pickableModNumbers?: Set<number>;
}

const isPogItem = (item: any): boolean => {
  return (
    item &&
    typeof item === 'object' &&
    'x1' in item &&
    'y1' in item &&
    'x2' in item &&
    'y2' in item &&
    'upc' in item &&
    'name' in item &&
    'ilItemId' in item &&
    !('shape' in item)
  );
};

const isTemplateItem = (item: any): boolean => {
  return (
    item &&
    typeof item === 'object' &&
    'bboxId' in item &&
    'x1' in item &&
    'y1' in item &&
    'x2' in item &&
    'y2' in item &&
    'upc' in item &&
    'tagName' in item &&
    'isActive' in item &&
    'productId' in item
  );
};

const isProductRecognitionItem = (
  item: any
): item is ProductRecognitionBBox | ProductRecognitionPolygon => {
  return (
    item &&
    typeof item === 'object' &&
    (('x1' in item && 'y1' in item && 'x2' in item && 'y2' in item) || 'points' in item) &&
    !isPogItem(item) &&
    !isTemplateItem(item)
  );
};

export const GtImageItemDetailsDialog: React.FC<GtImageItemDetailsDialogProps> = ({
  item,
  cameraImageElement,
  isOpen,
  onClose,
  layerBboxColor,
  chainId,
  storeId,
  storeName,
  onUpcSubmit,
  shouldShowEmbeddings = false,
  planogramComplianceData,
  cameraImage,
  cartonBoxes,
  pogStatus,
  onPogStatusApply,
  rogPositionKey,
  rogPositionStatuses,
  rogPositionFinalItemNumber,
  rogPositionPrUuidCount,
  onRogStatusApply,
  onRemoveRogPosition,
  onMoveRogPosition,
  pickableModNumbers,
}) => {
  const sortedPickableMods = React.useMemo(() => {
    if (!pickableModNumbers) return null;
    return [...pickableModNumbers].sort((a, b) => a - b);
  }, [pickableModNumbers]);
  const parsedRogPosition = React.useMemo(
    () => (rogPositionKey ? parsePositionKey(rogPositionKey) : null),
    [rogPositionKey]
  );

  const [moveMod, setMoveMod] = React.useState<string>('');
  const [moveShelf, setMoveShelf] = React.useState<string>('');
  const [moveColumn, setMoveColumn] = React.useState<string>('');

  React.useEffect(() => {
    if (parsedRogPosition) {
      setMoveMod(String(parsedRogPosition.mod));
      setMoveShelf(String(parsedRogPosition.shelf));
      setMoveColumn(String(parsedRogPosition.column));
    }
  }, [parsedRogPosition]);

  if (!isOpen) return null;

  const showRogEditor = !!rogPositionKey && !!onRogStatusApply;

  const handleUpcSubmit = (upc: string) => {
    const trimmedUpc = upc.trim();
    if (!trimmedUpc) return;
    onUpcSubmit?.(trimmedUpc);
  };

  const showPogEditor = isPogItem(item) && onPogStatusApply && typeof item.ilItemId === 'number';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0 !mb-0">
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden ${
          isProductRecognitionItem(item) || isPogItem(item) || isTemplateItem(item)
            ? 'max-w-5xl'
            : 'max-w-2xl'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {showRogEditor
              ? 'ROG Position Details'
              : isProductRecognitionItem(item)
                ? 'Detection Details'
                : isPogItem(item)
                  ? 'POG Product Details'
                  : isTemplateItem(item)
                    ? 'Template Product Details'
                    : 'Item Details'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {showRogEditor ? (
            <div className="p-6 space-y-4">
              <GtRogStatusEditor
                positionKey={rogPositionKey!}
                currentStatuses={rogPositionStatuses ?? []}
                onApply={(key, statuses) => {
                  onRogStatusApply!(key, statuses);
                  onClose();
                }}
              />
              {parsedRogPosition && onMoveRogPosition && (
                <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="text-sm font-medium text-gray-700">Move position</div>
                  <div className="flex flex-wrap items-center gap-2 w-full">
                    <label className="text-xs text-gray-600">Mod</label>
                    {sortedPickableMods ? (
                      <select
                        value={moveMod}
                        onChange={(e) => setMoveMod(e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded"
                      >
                        {!sortedPickableMods.includes(parsedRogPosition.mod) && (
                          <option value={parsedRogPosition.mod}>Mod {parsedRogPosition.mod}</option>
                        )}
                        {sortedPickableMods.map((mod) => (
                          <option key={mod} value={mod}>
                            Mod {mod}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number"
                        min={1}
                        value={moveMod}
                        onChange={(e) => setMoveMod(e.target.value)}
                        className="w-20 text-xs px-2 py-1 border border-gray-300 rounded"
                      />
                    )}
                    <label className="text-xs text-gray-600">Shelf</label>
                    <input
                      type="number"
                      min={1}
                      value={moveShelf}
                      onChange={(e) => setMoveShelf(e.target.value)}
                      className="w-20 text-xs px-2 py-1 border border-gray-300 rounded"
                    />
                    <label className="text-xs text-gray-600">Position</label>
                    <input
                      type="number"
                      min={1}
                      value={moveColumn}
                      onChange={(e) => setMoveColumn(e.target.value)}
                      className="w-20 text-xs px-2 py-1 border border-gray-300 rounded"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const toMod = Number(moveMod);
                        const toShelf = Number(moveShelf);
                        const toColumn = Number(moveColumn);
                        if (
                          !Number.isFinite(toMod) ||
                          !Number.isFinite(toShelf) ||
                          !Number.isFinite(toColumn) ||
                          toMod <= 0 ||
                          toShelf <= 0 ||
                          toColumn <= 0
                        )
                          return;
                        if (
                          toMod === parsedRogPosition.mod &&
                          toShelf === parsedRogPosition.shelf &&
                          toColumn === parsedRogPosition.column
                        ) {
                          onClose();
                          return;
                        }
                        onMoveRogPosition(
                          parsedRogPosition.mod,
                          parsedRogPosition.shelf,
                          parsedRogPosition.column,
                          toMod,
                          toShelf,
                          toColumn
                        );
                        onClose();
                      }}
                      className="text-xs px-3 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      Move
                    </button>
                    {onRemoveRogPosition && (
                      <button
                        type="button"
                        onClick={() => {
                          onRemoveRogPosition(
                            parsedRogPosition.mod,
                            parsedRogPosition.shelf,
                            parsedRogPosition.column
                          );
                          onClose();
                        }}
                        className="text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 ml-auto"
                      >
                        Remove position
                      </button>
                    )}
                  </div>
                </div>
              )}
              {parsedRogPosition && !onMoveRogPosition && onRemoveRogPosition && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      onRemoveRogPosition(
                        parsedRogPosition.mod,
                        parsedRogPosition.shelf,
                        parsedRogPosition.column
                      );
                      onClose();
                    }}
                    className="text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                  >
                    Remove position
                  </button>
                </div>
              )}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                <div>
                  <span className="font-medium text-gray-600">Position key:</span>{' '}
                  <span className="font-mono">{rogPositionKey}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Final item number:</span>{' '}
                  <span className="font-mono">{rogPositionFinalItemNumber ?? '—'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">PR detections linked:</span>{' '}
                  {rogPositionPrUuidCount ?? 0}
                </div>
              </div>
            </div>
          ) : isPogItem(item) || isTemplateItem(item) ? (
            <>
              {showPogEditor && (
                <div className="px-6 pt-6">
                  <GtPogStatusEditor
                    ilItemId={item.ilItemId as number}
                    currentStatus={pogStatus ?? null}
                    onApply={(ilItemId, status) => {
                      onPogStatusApply!(ilItemId, status);
                      onClose();
                    }}
                  />
                </div>
              )}
              <PogProductContent
                item={item}
                chainId={chainId}
                storeId={storeId}
                storeName={storeName}
                shouldShowEmbeddings={shouldShowEmbeddings}
              />
            </>
          ) : isProductRecognitionItem(item) && cameraImageElement ? (
            <div className="p-6">
              <ProductRecognitionDetectionContent
                item={item}
                cameraImageElement={cameraImageElement}
                layerBboxColor={layerBboxColor}
                chainId={chainId}
                storeId={storeId}
                onUpcSubmit={onUpcSubmit ? handleUpcSubmit : undefined}
                planogramComplianceData={planogramComplianceData}
                cameraImage={cameraImage}
                cartonBoxes={cartonBoxes}
                withReferenceCrops
              />
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="py-2">
                  <span className="text-gray-600 font-medium block mb-2">Raw Data:</span>
                  <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-200 overflow-y-auto">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-200 overflow-y-auto">
                {JSON.stringify(item, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
